import { cn } from "@/lib/utils";
import type { IconProps } from "@/types";

interface LogoProps extends IconProps {
  /** Capital-S mark only (`logos/sequelizer-mark-*.svg`), no wordmark text. */
  variant?: "wordmark" | "mark";
}

const wordmarkImgClass =
  "h-full max-h-full w-auto max-w-full object-contain object-center";

/**
 * Cache-bust: early Sequelizer deploys served a Kriya-K + "Sequelizer" hybrid at
 * these paths with `max-age=2592000`. Query forces browsers past that cache.
 */
const LOGO_ASSET_VERSION = "20261101";

function logoSrc(path: string): string {
  return `${path}?v=${LOGO_ASSET_VERSION}`;
}

/**
 * Official Sequelizer branding from `/public/images/logos/`.
 * Wordmark: outlined path SVGs (no embedded font — reliable in `<img>`).
 * Mark: `sequelizer-mark-*.svg` (solid S).
 */
const Logo = ({
  className,
  variant = "wordmark",
}: LogoProps): JSX.Element => {
  if (variant === "mark") {
    return (
      <span
        className={cn(
          "inline-flex size-8 shrink-0 items-center justify-center",
          className,
        )}
        role="img"
        aria-label="Sequelizer"
      >
        <img
          src={logoSrc("/images/logos/sequelizer-mark-dark.svg")}
          alt=""
          width={128}
          height={128}
          className="size-full object-contain dark:hidden"
          decoding="async"
          fetchPriority="low"
        />
        <img
          src={logoSrc("/images/logos/sequelizer-mark-light.svg")}
          alt=""
          width={128}
          height={128}
          className="hidden size-full object-contain dark:block"
          decoding="async"
          fetchPriority="low"
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden",
        "h-6 w-auto max-h-6",
        className,
      )}
      role="img"
      aria-label="Sequelizer"
    >
      <img
        src={logoSrc("/images/logos/sequelizer-logo-dark-sm.svg")}
        alt=""
        width={146}
        height={35}
        className={cn(wordmarkImgClass, "dark:hidden")}
        decoding="async"
        fetchPriority="low"
      />
      <img
        src={logoSrc("/images/logos/sequelizer-logo-light-sm.svg")}
        alt=""
        width={146}
        height={35}
        className={cn(wordmarkImgClass, "hidden dark:block")}
        decoding="async"
        fetchPriority="low"
      />
    </span>
  );
};

export default Logo;
